// Cliente do checkout nativo para a vitrine (2026-10-13).
//
// Módulo PURO (sem React, sem "server-only", sem fetch global), no mesmo
// padrão de lib/commerce/nativeCartClient.ts: monta as requisições para
// /api/checkout/native/*, valida as respostas, traduz erros em uma ação de
// recuperação e calcula o total exibido. O transporte é injetado
// (`NativeCheckoutSend`), então toda a sequência roda no test runner do
// Node sem servidor. components/Checkout/NativeCheckoutClient.tsx só liga
// isto ao fetch e ao estado da tela.
//
// Nunca fala com as rotas de pagamento/cliente do checkout legado nem com
// o WooCommerce: as únicas URLs montadas aqui são as rotas nativas abaixo.
//
// Sequência (scripts/staging/gate3-cart-checkout-smoke-test.mjs):
//   prepare -> pii -> [quote -> select] -> ready -> submit
// O carrinho fica travado ("locked") a partir do prepare; depois do ready o
// checkout não aceita mais PII/frete novos. Qualquer edição depois do ready,
// ou um erro de checkout desatualizado/expirado, recomeça do prepare --
// liberando antes o checkout antigo (POST /api/checkout/native/release).

import { isPaymentMethodAvailable } from "../../components/Checkout/paymentMethod.ts";
import type { CheckoutFormValues } from "../../types/checkout.ts";
import { computeOrderTotalsWithCoupon } from "../coupons/couponMath.ts";
import { couponItemDiscountFor, couponShippingDiscountFor, type NativeCartCouponView } from "../coupons/couponView.ts";

export const NATIVE_CHECKOUT_ENDPOINTS = {
  cart: "/api/cart/native",
  prepare: "/api/checkout/native/prepare",
  pii: "/api/checkout/native/pii",
  quote: "/api/checkout/native/shipping/quote",
  select: "/api/checkout/native/shipping/select",
  ready: "/api/checkout/native/ready",
  submit: "/api/checkout/native",
  release: "/api/checkout/native/release",
} as const;

export const NATIVE_CHECKOUT_GENERIC_ERROR = "Não foi possível continuar agora. Tente novamente.";
export const NATIVE_CHECKOUT_PAYMENT_ERROR = "Não foi possível concluir o pagamento. Tente novamente.";
export const NATIVE_CHECKOUT_PIX_DISCOUNT_BPS = 1000;
// Mesmo valor de native_payment_discount_bps('inter_boleto') no banco.
export const NATIVE_CHECKOUT_BOLETO_DISCOUNT_BPS = 500;
// Prazo do boleto do Banco Inter (BOLETO_DUE_DAYS em
// services/payments/inter/boleto.ts; há teste que confere os dois). Só
// exibição -- a data real vem do provedor.
export const NATIVE_CHECKOUT_BOLETO_DUE_DAYS = 2;

export interface NativeCheckoutRequest {
  url: string;
  method: "GET" | "POST";
  body?: Record<string, unknown>;
}

export interface NativeCheckoutHttpResult {
  status: number;
  ok: boolean;
  body: unknown;
}

export type NativeCheckoutSend = (request: NativeCheckoutRequest) => Promise<NativeCheckoutHttpResult>;

// O que a tela deve fazer depois de um erro:
//  - restart: liberar o checkout atual e recomeçar do prepare (dados do
//    formulário preservados; volta para a etapa de dados);
//  - requote: pedir nova cotação de frete (checkout ainda não "ready");
//  - edit: corrigir um campo do formulário (CHECKOUT_PII_INVALID);
//  - cart: voltar ao carrinho (vazio, item sem estoque);
//  - retry: tentar de novo a mesma ação;
//  - none: nada a refazer automaticamente (ex.: pagamento recusado).
export type NativeCheckoutRecovery = "restart" | "requote" | "edit" | "cart" | "retry" | "none";

export interface NativeCheckoutFailure {
  kind: "error";
  status: number;
  code: string;
  message: string;
  field?: string;
  recovery: NativeCheckoutRecovery;
}

const RESTART_CODES = new Set([
  "CHECKOUT_STALE",
  "CHECKOUT_EXPIRED",
  "CHECKOUT_NOT_FOUND",
  "CHECKOUT_NOT_READY",
  "CHECKOUT_PII_REQUIRED",
  "CHECKOUT_CART_LOCKED",
  "CHECKOUT_SHIPPING_NOT_REQUIRED",
]);
const CART_CODES = new Set(["CART_EMPTY", "CART_NOT_FOUND", "CART_ITEM_INSUFFICIENT_STOCK", "CART_STOCK_DATA_STALE"]);
const NO_RETRY_CODES = new Set([
  "PAYMENT_REJECTED",
  "CHECKOUT_IDEMPOTENCY_CONFLICT",
  "CHECKOUT_PAYMENT_METHOD_CHANGED",
  "CHECKOUT_SHIPPING_ITEMS_UNQUOTABLE",
  "CHECKOUT_SHIPPING_NO_OPTIONS",
  "UNKNOWN_PAYMENT_METHOD",
  // Cinto de segurança do servidor (a tela já esconde o boleto abaixo do
  // mínimo): só mostra a mensagem.
  "BOLETO_BELOW_MINIMUM",
  "CARD_INSTALLMENTS_ABOVE_LIMIT",
  "CHECKOUT_INSTALLMENTS_CHANGED",
]);

export function recoveryForCode(code: string): NativeCheckoutRecovery {
  if (RESTART_CODES.has(code)) return "restart";
  // Cupons nativos: nenhum pedido foi criado; a tela atualiza o cupom e
  // mostra a mensagem (o cliente remove o cupom ou confere o total).
  if (code === "CHECKOUT_COUPON_CHANGED" || code.startsWith("COUPON_")) return "none";
  if (code === "CHECKOUT_SHIPPING_QUOTE_INVALID") return "requote";
  if (code === "CHECKOUT_PII_INVALID") return "edit";
  if (CART_CODES.has(code)) return "cart";
  if (NO_RETRY_CODES.has(code)) return "none";
  return "retry";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function isVersionString(value: unknown): value is string {
  return typeof value === "string" && /^\d{1,20}$/.test(value);
}

// Usa a mensagem em português que o próprio servidor devolve (as rotas
// nativas nunca devolvem texto interno de banco/provedor); sem ela, um
// texto genérico.
export function readNativeCheckoutError(result: NativeCheckoutHttpResult, fallback = NATIVE_CHECKOUT_GENERIC_ERROR): NativeCheckoutFailure {
  const body = isRecord(result.body) ? result.body : {};
  const code = isNonEmptyString(body.code) ? body.code : result.status === 429 ? "RATE_LIMITED" : "UNKNOWN";
  const message = isNonEmptyString(body.message) ? body.message : fallback;
  const field = isNonEmptyString(body.field) ? body.field : undefined;
  return { kind: "error", status: result.status, code, message, field, recovery: recoveryForCode(code) };
}

// O servidor aponta o campo inválido como "contact.phone",
// "billing.postalCode" etc. (CheckoutPiiValidationError) -- traduz para o
// caminho do formulário da tela, para focar o campo certo.
const PII_FIELD_TO_FORM_PATH: Record<string, string> = {
  "contact.firstName": "contact.firstName",
  "contact.lastName": "contact.lastName",
  "contact.email": "contact.email",
  "contact.phone": "contact.phone",
  "contact.personType": "contact.document",
  "contact.taxDocument": "contact.document",
  "contact.company": "contact.company",
  "billing.recipient": "billingAddress.recipientName",
  "billing.street": "billingAddress.addressLine1",
  "billing.number": "billingAddress.number",
  "billing.complement": "billingAddress.addressLine2",
  "billing.neighborhood": "billingAddress.neighborhood",
  "billing.city": "billingAddress.city",
  "billing.state": "billingAddress.state",
  "billing.postalCode": "billingAddress.postalCode",
};

export function mapPiiFieldToFormPath(field: string | undefined): string | null {
  if (!field) return null;
  return PII_FIELD_TO_FORM_PATH[field] ?? null;
}

// ---------- PII ----------

export interface NativeCheckoutAddressInput {
  recipient: string;
  company: string | null;
  street: string;
  number: string;
  complement: string | null;
  neighborhood: string;
  city: string;
  state: string;
  postalCode: string;
  country: "BR";
}

export interface NativeCheckoutPiiInput {
  contact: {
    firstName: string;
    lastName: string;
    company: string | null;
    email: string;
    phone: string;
    personType: "fisica" | "juridica";
    taxDocument: string;
  };
  billing: NativeCheckoutAddressInput;
  shipping: NativeCheckoutAddressInput;
  shippingSameAsBilling: boolean;
}

function optional(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function toAddress(address: CheckoutFormValues["billingAddress"], company: string | null, fallbackRecipient: string): NativeCheckoutAddressInput {
  return {
    recipient: address.recipientName.trim() || fallbackRecipient,
    company,
    street: address.addressLine1.trim(),
    number: address.number.trim(),
    complement: optional(address.addressLine2),
    neighborhood: address.neighborhood.trim(),
    city: address.city.trim(),
    state: address.state.trim().toUpperCase(),
    postalCode: address.postalCode.replace(/\D/g, ""),
    country: "BR",
  };
}

// Formato exigido por POST /api/checkout/native/pii (lib/commerce/
// checkoutPii.ts, schema estrito). `shipping` é obrigatório mesmo quando
// igual à cobrança; a loja usa um único endereço.
export function buildNativeCheckoutPii(values: CheckoutFormValues): NativeCheckoutPiiInput {
  const company = optional(values.contact.company);
  const fullName = `${values.contact.firstName.trim()} ${values.contact.lastName.trim()}`.trim();
  const billing = toAddress(values.billingAddress, company, fullName);
  const sameAsBilling = values.shipToBillingAddress;
  return {
    contact: {
      firstName: values.contact.firstName.trim(),
      lastName: values.contact.lastName.trim(),
      company,
      email: values.contact.email.trim(),
      phone: values.contact.phone.trim(),
      personType: values.contact.personType,
      taxDocument: values.contact.document.replace(/\D/g, ""),
    },
    billing,
    shipping: sameAsBilling ? { ...billing } : toAddress(values.shippingAddress, company, fullName),
    shippingSameAsBilling: sameAsBilling,
  };
}

// ---------- requisições ----------

export function buildGetNativeCartIdRequest(): NativeCheckoutRequest {
  return { url: NATIVE_CHECKOUT_ENDPOINTS.cart, method: "GET" };
}

export function buildPrepareRequest(cartId: string, idempotencyKey: string): NativeCheckoutRequest {
  return { url: NATIVE_CHECKOUT_ENDPOINTS.prepare, method: "POST", body: { cartId, idempotencyKey } };
}

export function buildPiiRequest(checkoutId: string, expectedVersion: string, idempotencyKey: string, pii: NativeCheckoutPiiInput): NativeCheckoutRequest {
  return {
    url: NATIVE_CHECKOUT_ENDPOINTS.pii,
    method: "POST",
    body: { checkoutId, expectedVersion, idempotencyKey, pii: { ...pii } },
  };
}

export function buildQuoteRequest(checkoutId: string, expectedVersion: string): NativeCheckoutRequest {
  return { url: NATIVE_CHECKOUT_ENDPOINTS.quote, method: "POST", body: { checkoutId, expectedVersion } };
}

export function buildSelectRequest(checkoutId: string, expectedVersion: string, optionId: string): NativeCheckoutRequest {
  return { url: NATIVE_CHECKOUT_ENDPOINTS.select, method: "POST", body: { checkoutId, expectedVersion, optionId } };
}

export function buildReadyRequest(checkoutId: string, expectedVersion: string, expectedPiiFingerprint: string): NativeCheckoutRequest {
  return { url: NATIVE_CHECKOUT_ENDPOINTS.ready, method: "POST", body: { checkoutId, expectedVersion, expectedPiiFingerprint } };
}

export type NativeCheckoutPayment =
  | { method: "inter_pix" }
  | { method: "inter_boleto" }
  | { method: "mercadopago_card"; cardToken: string; installments: number; paymentMethodId: string; issuerId?: string };

export type NativeCheckoutPaymentMethod = NativeCheckoutPayment["method"];

// `couponCode` (cupons nativos): código que a tela mostra como aplicado
// ("" = nenhum). undefined = não envia (cupons desligados), corpo idêntico
// ao anterior.
export function buildSubmitRequest(checkoutId: string, expectedVersion: string, payment: NativeCheckoutPayment, couponCode?: string, customerNote?: string): NativeCheckoutRequest {
  // A rota de submissão é estrita: issuerId só vai quando existe. Pix e
  // boleto não levam mais nada -- nome e documento do pagador o servidor
  // tira dos dados pessoais já guardados (PII).
  const paymentBody: Record<string, unknown> =
    payment.method === "inter_pix" || payment.method === "inter_boleto"
      ? { method: payment.method }
      : {
          method: "mercadopago_card",
          cardToken: payment.cardToken,
          installments: payment.installments,
          paymentMethodId: payment.paymentMethodId,
          ...(payment.issuerId ? { issuerId: payment.issuerId } : {}),
        };
  const body: Record<string, unknown> = { checkoutId, expectedVersion, payment: paymentBody };
  if (couponCode !== undefined) body.couponCode = couponCode ? couponCode : null;
  const note = customerNote?.trim().slice(0, 500);
  if (note) body.customerNote = note;
  return { url: NATIVE_CHECKOUT_ENDPOINTS.submit, method: "POST", body };
}

export function buildReleaseRequest(): NativeCheckoutRequest {
  return { url: NATIVE_CHECKOUT_ENDPOINTS.release, method: "POST", body: {} };
}

// includePaymentDetails (?qr=1): QR do Pix ou linha digitável do boleto.
export function buildNativeOrderStatusUrl(orderId: string, statusToken: string, includePaymentDetails: boolean): string {
  const params = new URLSearchParams({ t: statusToken });
  if (includePaymentDetails) params.set("qr", "1");
  return `/api/checkout/native/orders/${encodeURIComponent(orderId)}/status?${params.toString()}`;
}

export function buildNativeConfirmationUrl(orderId: string, statusToken: string): string {
  const params = new URLSearchParams({ pedido: orderId, t: statusToken });
  return `/checkout/confirmacao?${params.toString()}`;
}

// ---------- respostas ----------

export function parseNativeCartId(body: unknown): string | null {
  return isRecord(body) && isNonEmptyString(body.id) ? body.id : null;
}

export interface PrepareResponse {
  checkoutId: string;
  version: string;
  shippingRequired: boolean;
}

export function parsePrepareResponse(body: unknown): PrepareResponse | null {
  if (!isRecord(body) || !isNonEmptyString(body.checkoutId) || !isVersionString(body.version)) return null;
  return { checkoutId: body.checkoutId, version: body.version, shippingRequired: body.shippingRequired === true };
}

export interface PiiResponse {
  checkoutVersion: string;
  fingerprint: string;
}

export function parsePiiResponse(body: unknown): PiiResponse | null {
  if (!isRecord(body) || !isVersionString(body.checkoutVersion)) return null;
  if (typeof body.fingerprint !== "string" || !/^[0-9a-f]{64}$/.test(body.fingerprint)) return null;
  return { checkoutVersion: body.checkoutVersion, fingerprint: body.fingerprint };
}

export type NativeShippingOptionKind = "pickup" | "local_delivery" | "carrier";

export interface NativeShippingOptionView {
  optionId: string;
  kind: NativeShippingOptionKind;
  carrier: string;
  serviceName: string;
  /** Nome exibido ao cliente, definido pelo servidor ("Frete Econômico"); opcional em respostas antigas. */
  label?: string | null;
  amountMinor: number;
  estimatedDays: number;
  expiresAt: string;
  /** Texto no lugar do prazo (retirada/Entrega Persi). */
  description: string | null;
  /** Endereço da loja (só retirada). */
  pickupAddress: string | null;
}

const SHIPPING_KINDS: ReadonlySet<string> = new Set<NativeShippingOptionKind>(["pickup", "local_delivery", "carrier"]);

function parseShippingOption(value: unknown): NativeShippingOptionView | null {
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.optionId) ||
    typeof value.carrier !== "string" ||
    typeof value.serviceName !== "string" ||
    typeof value.amountMinor !== "number" ||
    !Number.isInteger(value.amountMinor) ||
    value.amountMinor < 0 ||
    typeof value.estimatedDays !== "number" ||
    typeof value.expiresAt !== "string"
  ) {
    return null;
  }
  // Resposta antiga (sem `kind`) = transportadora.
  const kind = value.kind === undefined ? "carrier" : typeof value.kind === "string" && SHIPPING_KINDS.has(value.kind) ? value.kind : null;
  if (!kind) return null;
  return {
    optionId: value.optionId,
    kind: kind as NativeShippingOptionKind,
    carrier: value.carrier,
    serviceName: value.serviceName,
    label: isNonEmptyString(value.label) ? value.label : null,
    amountMinor: value.amountMinor,
    estimatedDays: value.estimatedDays,
    expiresAt: value.expiresAt,
    description: isNonEmptyString(value.description) ? value.description : null,
    pickupAddress: isNonEmptyString(value.pickupAddress) ? value.pickupAddress : null,
  };
}

// Mesma ordem do servidor (lib/shipping/shippingOptionSelection.ts):
// retirada na loja primeiro, depois da mais barata para a mais cara (entrega
// própria e transportadora juntas; empate: menor prazo, depois a ordem do
// servidor).
export function sortShippingOptions(options: NativeShippingOptionView[]): NativeShippingOptionView[] {
  const days = (option: NativeShippingOptionView) => (option.estimatedDays > 0 ? option.estimatedDays : Number.POSITIVE_INFINITY);
  return options
    .map((option, index) => ({ option, index }))
    .sort(
      (left, right) =>
        Number(right.option.kind === "pickup") - Number(left.option.kind === "pickup") ||
        left.option.amountMinor - right.option.amountMinor ||
        (days(left.option) === days(right.option) ? 0 : days(left.option) < days(right.option) ? -1 : 1) ||
        left.index - right.index,
    )
    .map(({ option }) => option);
}

// Pré-seleção: a opção de ENTREGA mais barata. A retirada (R$ 0,00) nunca
// vem marcada -- precisa ser escolha explícita do cliente. Só retirada
// disponível -> nada pré-selecionado.
export function defaultShippingOptionId(options: NativeShippingOptionView[]): string | null {
  const cheapest = options.filter((option) => option.kind !== "pickup").sort((left, right) => left.amountMinor - right.amountMinor || left.estimatedDays - right.estimatedDays)[0];
  return cheapest?.optionId ?? null;
}

export interface QuoteResponse {
  options: NativeShippingOptionView[];
  carrierNotice: string | null;
}

export function parseQuoteResponseDetailed(body: unknown): QuoteResponse | null {
  if (!isRecord(body) || !Array.isArray(body.options)) return null;
  const options = body.options.map(parseShippingOption);
  if (options.some((option) => option === null)) return null;
  const notice = isRecord(body.carrierNotice) && isNonEmptyString(body.carrierNotice.message) ? body.carrierNotice.message : null;
  return { options: sortShippingOptions(options as NativeShippingOptionView[]), carrierNotice: notice };
}

export function parseQuoteResponse(body: unknown): NativeShippingOptionView[] | null {
  return parseQuoteResponseDetailed(body)?.options ?? null;
}

export interface SelectResponse {
  shippingAmountMinor: number;
  itemsSubtotalMinor: number | null;
}

export function parseSelectResponse(body: unknown): SelectResponse | null {
  if (!isRecord(body) || typeof body.shippingAmountMinor !== "number" || !Number.isInteger(body.shippingAmountMinor)) return null;
  const subtotal = body.itemsSubtotalMinor;
  return {
    shippingAmountMinor: body.shippingAmountMinor,
    itemsSubtotalMinor: typeof subtotal === "number" && Number.isInteger(subtotal) ? subtotal : null,
  };
}

export function parseReadyResponse(body: unknown): string | null {
  return isRecord(body) && isVersionString(body.version) ? body.version : null;
}

export interface SubmitResponse {
  method: string;
  orderId: string;
  orderNumber: string;
  paymentStatus: string;
  amountMinor: string;
  statusToken: string | null;
}

export function parseSubmitResponse(body: unknown): SubmitResponse | null {
  if (!isRecord(body) || !isNonEmptyString(body.orderId) || typeof body.orderNumber !== "string") return null;
  return {
    method: typeof body.method === "string" ? body.method : "",
    orderId: body.orderId,
    orderNumber: body.orderNumber,
    paymentStatus: typeof body.paymentStatus === "string" ? body.paymentStatus : "",
    amountMinor: typeof body.amountMinor === "string" ? body.amountMinor : "0",
    statusToken: typeof body.statusToken === "string" && /^[0-9a-f]{64}$/.test(body.statusToken) ? body.statusToken : null,
  };
}

export interface NativeOrderStatusView {
  orderNumber: string;
  orderStatus: string;
  paymentStatus: string | null;
  method: string | null;
  amountMinor: string;
  pix: { qrCodeCopyPaste: string; qrCodeImageBase64: string; expiresAt: string } | null;
  boleto: { digitableLine: string; barcode: string; dueDate: string } | null;
  /** Retirada na loja (com endereço) ou Entrega Persi; null = transportadora/sem frete. */
  fulfillment:
    | { kind: "pickup"; label: string; address: string; note: string }
    | { kind: "local_delivery"; label: string }
    | null;
}

function parseOrderFulfillment(value: unknown): NativeOrderStatusView["fulfillment"] {
  if (!isRecord(value) || !isNonEmptyString(value.label)) return null;
  if (value.kind === "pickup" && isNonEmptyString(value.address) && typeof value.note === "string") {
    return { kind: "pickup", label: value.label, address: value.address, note: value.note };
  }
  if (value.kind === "local_delivery") return { kind: "local_delivery", label: value.label };
  return null;
}

export function parseNativeOrderStatus(body: unknown): NativeOrderStatusView | null {
  if (!isRecord(body) || typeof body.orderNumber !== "string" || typeof body.orderStatus !== "string") return null;
  const pix = isRecord(body.pix) &&
    isNonEmptyString(body.pix.qrCodeCopyPaste) &&
    isNonEmptyString(body.pix.qrCodeImageBase64) &&
    typeof body.pix.expiresAt === "string"
    ? { qrCodeCopyPaste: body.pix.qrCodeCopyPaste, qrCodeImageBase64: body.pix.qrCodeImageBase64, expiresAt: body.pix.expiresAt }
    : null;
  const boleto = isRecord(body.boleto) &&
    isNonEmptyString(body.boleto.digitableLine) &&
    typeof body.boleto.barcode === "string" &&
    typeof body.boleto.dueDate === "string"
    ? { digitableLine: body.boleto.digitableLine, barcode: body.boleto.barcode, dueDate: body.boleto.dueDate }
    : null;
  return {
    orderNumber: body.orderNumber,
    orderStatus: body.orderStatus,
    paymentStatus: typeof body.paymentStatus === "string" ? body.paymentStatus : null,
    method: typeof body.method === "string" ? body.method : null,
    amountMinor: typeof body.amountMinor === "string" ? body.amountMinor : "0",
    pix,
    boleto,
    fulfillment: parseOrderFulfillment(body.fulfillment),
  };
}

export type NativePaymentCategory = "pending" | "paid" | "failed";

// Status do pedido (orders.status) + da tentativa de pagamento mais recente
// (payment_attempts.status) -> o que a tela de confirmação mostra.
export function categorizeNativeOrderStatus(status: Pick<NativeOrderStatusView, "orderStatus" | "paymentStatus">): NativePaymentCategory {
  if (status.paymentStatus === "paid" || status.orderStatus === "confirmed" || status.orderStatus === "completed") return "paid";
  if (status.orderStatus === "cancelled") return "failed";
  if (
    status.paymentStatus === "failed" ||
    status.paymentStatus === "cancelled" ||
    status.paymentStatus === "expired" ||
    status.paymentStatus === "refunded" ||
    status.paymentStatus === "partially_refunded"
  ) {
    return "failed";
  }
  return "pending";
}

// ---------- totais ----------

export interface NativeCheckoutTotals {
  itemsSubtotalMinor: number;
  paymentDiscountMinor: number;
  shippingMinor: number;
  totalMinor: number;
  // Só com cupom nativo aplicado.
  couponItemDiscountMinor?: number;
  couponShippingDiscountMinor?: number;
  /** false = cupom não cumulativo perdeu para o desconto do Pix/boleto. */
  couponApplied?: boolean;
}

export function nativePaymentDiscountBps(method: NativeCheckoutPaymentMethod): number {
  if (method === "inter_pix") return NATIVE_CHECKOUT_PIX_DISCOUNT_BPS;
  if (method === "inter_boleto") return NATIVE_CHECKOUT_BOLETO_DISCOUNT_BPS;
  return 0;
}

// Mesma conta de submit_native_checkout_v2 (migration 20261013080000):
// desconto = round(subtotal dos produtos * bps / 10000), arredondando a
// metade para cima, SÓ sobre os produtos; frete integral. Inteiros em
// centavos -- sem ponto flutuante.
//
// Com cupom nativo (NATIVE_COUPONS=on): mesma conta de
// submit_native_checkout_v3 (lib/coupons/couponMath.ts) -- cupom primeiro e
// Pix/boleto sobre o restante dos produtos, ou só o maior dos dois no modo
// "não cumulativo". Sem cupom, resultado idêntico ao anterior.
export function computeNativeCheckoutTotals(input: {
  itemsSubtotalMinor: number;
  shippingMinor: number | null;
  method: NativeCheckoutPaymentMethod;
  coupon?: NativeCartCouponView | null;
}): NativeCheckoutTotals {
  const subtotal = Math.max(0, Math.trunc(input.itemsSubtotalMinor));
  const shipping = Math.max(0, Math.trunc(input.shippingMinor ?? 0));
  const bps = nativePaymentDiscountBps(input.method);
  if (input.coupon?.applied) {
    const totals = computeOrderTotalsWithCoupon({
      itemsSubtotalMinor: subtotal,
      shippingMinor: shipping,
      couponItemDiscountMinor: Math.min(couponItemDiscountFor(input.coupon), subtotal),
      couponShippingDiscountMinor: couponShippingDiscountFor(input.coupon, input.shippingMinor),
      pixMode: input.coupon.pixMode,
      paymentDiscountBps: bps,
    });
    return {
      itemsSubtotalMinor: subtotal,
      paymentDiscountMinor: totals.paymentDiscountMinor,
      shippingMinor: shipping,
      totalMinor: totals.grandTotalMinor,
      couponItemDiscountMinor: totals.couponItemDiscountMinor,
      couponShippingDiscountMinor: totals.couponShippingDiscountMinor,
      couponApplied: totals.couponApplied,
    };
  }
  const discount = Math.floor((subtotal * bps + 5000) / 10000);
  return { itemsSubtotalMinor: subtotal, paymentDiscountMinor: discount, shippingMinor: shipping, totalMinor: subtotal - discount + shipping };
}

// Forma de pagamento disponível para este total? Reaproveita a regra do
// checkout legado (isPaymentMethodAvailable: hoje só o boleto tem mínimo,
// MIN_BOLETO_AMOUNT = R$ 2,50 JÁ com o desconto) com o total do pedido
// (produtos + frete) e a base de desconto (só produtos).
export function isNativePaymentMethodAvailable(
  method: NativeCheckoutPaymentMethod,
  input: { itemsSubtotalMinor: number; shippingMinor: number | null },
): boolean {
  const subtotal = Math.max(0, Math.trunc(input.itemsSubtotalMinor));
  const shipping = Math.max(0, Math.trunc(input.shippingMinor ?? 0));
  return isPaymentMethodAvailable(method, (subtotal + shipping) / 100, subtotal / 100);
}

// CartMoney.value já vem em centavos (string inteira).
export function moneyValueToMinor(value: string): number {
  return /^\d+$/.test(value) ? Number.parseInt(value, 10) : 0;
}

export function formatMinorBRL(amountMinor: number | string): string {
  const value = typeof amountMinor === "string" ? Number.parseInt(amountMinor, 10) : amountMinor;
  const safe = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(safe / 100);
}

// ---------- estado do checkout (máquina de etapas) ----------

export type NativeCheckoutStep = "details" | "shipping" | "payment";

export interface NativeCheckoutSession {
  checkoutId: string;
  // Versão atual do checkout para o próximo expectedVersion (depois do pii,
  // é o checkoutVersion; o select não muda a versão).
  version: string;
  shippingRequired: boolean;
  piiFingerprint: string | null;
  options: NativeShippingOptionView[];
  selectedOptionId: string | null;
  shippingMinor: number | null;
  itemsSubtotalMinor: number | null;
  // Preenchido depois do ready: o checkout fica travado para PII/frete.
  readyVersion: string | null;
  // Transportadoras indisponíveis nesta cotação (a retirada seguiu
  // oferecida). Só texto para a tela; nunca afeta valores.
  carrierNotice?: string | null;
}

export function canEnterStep(step: NativeCheckoutStep, session: NativeCheckoutSession | null): boolean {
  if (step === "details") return true;
  if (!session || !session.piiFingerprint) return false;
  if (step === "shipping") return session.shippingRequired;
  return !session.shippingRequired || session.selectedOptionId !== null;
}

export function stepAfterDetails(session: NativeCheckoutSession): NativeCheckoutStep {
  return session.shippingRequired ? "shipping" : "payment";
}

// Sem checkout, ou checkout já "ready" (travado): precisa de um prepare novo.
export function needsFreshPrepare(session: NativeCheckoutSession | null): boolean {
  return !session || session.readyVersion !== null;
}

// Para onde a tela volta depois de um erro, e com qual sessão.
export function applyRecovery(
  session: NativeCheckoutSession | null,
  recovery: NativeCheckoutRecovery,
  currentStep: NativeCheckoutStep,
): { session: NativeCheckoutSession | null; step: NativeCheckoutStep } {
  if (recovery === "restart" || recovery === "cart") return { session: null, step: "details" };
  if (recovery === "requote") {
    // Checkout já "ready" não aceita nova cotação: recomeça.
    if (!session || session.readyVersion !== null) return { session: null, step: "details" };
    return { session: { ...session, options: [], selectedOptionId: null, shippingMinor: null }, step: "shipping" };
  }
  if (recovery === "edit") return { session, step: "details" };
  return { session, step: currentStep };
}

// ---------- sequência (transporte injetado) ----------

export type FlowResult<T> = { kind: "ok"; value: T } | NativeCheckoutFailure;

function protocolError(): NativeCheckoutFailure {
  return { kind: "error", status: 0, code: "INVALID_RESPONSE", message: NATIVE_CHECKOUT_GENERIC_ERROR, recovery: "retry" };
}

async function safeSend(send: NativeCheckoutSend, request: NativeCheckoutRequest): Promise<NativeCheckoutHttpResult | NativeCheckoutFailure> {
  try {
    return await send(request);
  } catch {
    return { kind: "error", status: 0, code: "NETWORK_ERROR", message: "Falha de conexão. Verifique sua internet e tente novamente.", recovery: "retry" };
  }
}

function isFailure(value: NativeCheckoutHttpResult | NativeCheckoutFailure): value is NativeCheckoutFailure {
  return "kind" in value && value.kind === "error";
}

// Libera checkouts abertos do carrinho do próprio convidado (o servidor
// acha o carrinho pelo cookie). Falha aqui nunca é fatal para quem chama.
export async function releaseNativeCheckout(send: NativeCheckoutSend): Promise<{ released: number } | NativeCheckoutFailure> {
  const result = await safeSend(send, buildReleaseRequest());
  if (isFailure(result)) return result;
  if (!result.ok) return readNativeCheckoutError(result);
  const released = isRecord(result.body) && typeof result.body.released === "number" ? result.body.released : 0;
  return { released };
}

function newSession(prepared: PrepareResponse): NativeCheckoutSession {
  return {
    checkoutId: prepared.checkoutId,
    version: prepared.version,
    shippingRequired: prepared.shippingRequired,
    piiFingerprint: null,
    options: [],
    selectedOptionId: null,
    shippingMinor: null,
    itemsSubtotalMinor: null,
    readyVersion: null,
  };
}

async function prepareOnce(send: NativeCheckoutSend, cartId: string, idempotencyKey: string): Promise<FlowResult<NativeCheckoutSession>> {
  const result = await safeSend(send, buildPrepareRequest(cartId, idempotencyKey));
  if (isFailure(result)) return result;
  if (!result.ok) return readNativeCheckoutError(result);
  const prepared = parsePrepareResponse(result.body);
  return prepared ? { kind: "ok", value: newSession(prepared) } : protocolError();
}

async function readCartId(send: NativeCheckoutSend): Promise<FlowResult<string>> {
  const result = await safeSend(send, buildGetNativeCartIdRequest());
  if (isFailure(result)) return result;
  if (!result.ok) return readNativeCheckoutError(result);
  const cartId = parseNativeCartId(result.body);
  return cartId
    ? { kind: "ok", value: cartId }
    : { kind: "error", status: 404, code: "CART_NOT_FOUND", message: "Seu carrinho está vazio.", recovery: "cart" };
}

// Garante um checkout aberto e editável. Reaproveita a sessão atual quando
// ela ainda não foi marcada "ready"; senão libera a antiga e prepara outra.
// Carrinho travado por outro checkout (outra aba, visita anterior): libera
// e tenta UMA vez de novo, com chave nova.
export async function ensureNativeCheckout(options: {
  send: NativeCheckoutSend;
  session: NativeCheckoutSession | null;
  newKey: () => string;
}): Promise<FlowResult<NativeCheckoutSession>> {
  const { send, session, newKey } = options;
  if (!needsFreshPrepare(session) && session) return { kind: "ok", value: session };
  if (session) await releaseNativeCheckout(send);

  let cart = await readCartId(send);
  if (cart.kind === "error") {
    // O GET só devolve carrinho "active": travado aparece como "sem carrinho".
    const released = await releaseNativeCheckout(send);
    if ("kind" in released || released.released === 0) return cart;
    cart = await readCartId(send);
    if (cart.kind === "error") return cart;
  }

  const first = await prepareOnce(send, cart.value, newKey());
  if (first.kind === "ok" || first.code !== "CHECKOUT_CART_LOCKED") return first;
  const released = await releaseNativeCheckout(send);
  if ("kind" in released) return first;
  const cartAfterRelease = await readCartId(send);
  if (cartAfterRelease.kind === "error") return cartAfterRelease;
  return prepareOnce(send, cartAfterRelease.value, newKey());
}

// Grava contato/endereço e, se o checkout tem frete, cota as opções.
export async function saveNativeCheckoutDetails(options: {
  send: NativeCheckoutSend;
  session: NativeCheckoutSession;
  pii: NativeCheckoutPiiInput;
  idempotencyKey: string;
}): Promise<FlowResult<NativeCheckoutSession>> {
  const { send, session, pii, idempotencyKey } = options;
  const piiResult = await safeSend(send, buildPiiRequest(session.checkoutId, session.version, idempotencyKey, pii));
  if (isFailure(piiResult)) return piiResult;
  if (!piiResult.ok) return readNativeCheckoutError(piiResult);
  const persisted = parsePiiResponse(piiResult.body);
  if (!persisted) return protocolError();

  const updated: NativeCheckoutSession = {
    ...session,
    version: persisted.checkoutVersion,
    piiFingerprint: persisted.fingerprint,
    options: [],
    selectedOptionId: null,
    shippingMinor: null,
  };
  if (!updated.shippingRequired) return { kind: "ok", value: updated };
  return quoteNativeShipping({ send, session: updated });
}

export async function quoteNativeShipping(options: {
  send: NativeCheckoutSend;
  session: NativeCheckoutSession;
}): Promise<FlowResult<NativeCheckoutSession>> {
  const { send, session } = options;
  const result = await safeSend(send, buildQuoteRequest(session.checkoutId, session.version));
  if (isFailure(result)) return result;
  if (!result.ok) return readNativeCheckoutError(result);
  const quoted = parseQuoteResponseDetailed(result.body);
  if (!quoted) return protocolError();
  if (quoted.options.length === 0) {
    return { kind: "error", status: 422, code: "CHECKOUT_SHIPPING_NO_OPTIONS", message: "Não encontramos opções de entrega para este endereço.", recovery: "none" };
  }
  return {
    kind: "ok",
    value: { ...session, options: quoted.options, selectedOptionId: null, shippingMinor: null, carrierNotice: quoted.carrierNotice },
  };
}

export async function selectNativeShippingOption(options: {
  send: NativeCheckoutSend;
  session: NativeCheckoutSession;
  optionId: string;
}): Promise<FlowResult<NativeCheckoutSession>> {
  const { send, session, optionId } = options;
  const result = await safeSend(send, buildSelectRequest(session.checkoutId, session.version, optionId));
  if (isFailure(result)) return result;
  if (!result.ok) return readNativeCheckoutError(result);
  const selected = parseSelectResponse(result.body);
  if (!selected) return protocolError();
  return {
    kind: "ok",
    value: {
      ...session,
      selectedOptionId: optionId,
      shippingMinor: selected.shippingAmountMinor,
      itemsSubtotalMinor: selected.itemsSubtotalMinor ?? session.itemsSubtotalMinor,
    },
  };
}

// ready (uma vez por checkout) -> submit. Em falha depois do ready, a
// sessão devolvida já carrega readyVersion, para uma nova tentativa (ex.:
// outro cartão) não repetir o ready.
export async function finalizeNativeCheckout(options: {
  send: NativeCheckoutSend;
  session: NativeCheckoutSession;
  payment: NativeCheckoutPayment;
  couponCode?: string;
  /** Observação do cliente ("Incluir informações adicionais"). */
  customerNote?: string;
}): Promise<{ session: NativeCheckoutSession; result: FlowResult<SubmitResponse> }> {
  let session = options.session;
  if (!canEnterStep("payment", session) || !session.piiFingerprint) {
    return { session, result: { kind: "error", status: 409, code: "CHECKOUT_NOT_READY", message: "Confirme seus dados antes de pagar.", recovery: "restart" } };
  }
  if (session.readyVersion === null) {
    const ready = await safeSend(options.send, buildReadyRequest(session.checkoutId, session.version, session.piiFingerprint));
    if (isFailure(ready)) return { session, result: ready };
    if (!ready.ok) return { session, result: readNativeCheckoutError(ready) };
    const readyVersion = parseReadyResponse(ready.body);
    if (!readyVersion) return { session, result: protocolError() };
    session = { ...session, readyVersion };
  }

  const submitted = await safeSend(options.send, buildSubmitRequest(session.checkoutId, session.readyVersion ?? session.version, options.payment, options.couponCode, options.customerNote));
  if (isFailure(submitted)) return { session, result: submitted };
  if (!submitted.ok) return { session, result: readNativeCheckoutError(submitted, NATIVE_CHECKOUT_PAYMENT_ERROR) };
  const parsed = parseSubmitResponse(submitted.body);
  return { session, result: parsed ? { kind: "ok", value: parsed } : protocolError() };
}

// ---------- analytics ----------

export interface NativeAnalyticsItem {
  item_id: string;
  /** Código do catálogo da Meta (produto pai), para o Pixel. */
  content_id?: string;
  item_name: string;
  price: number;
  quantity: number;
}

export function purchaseStorageKey(orderId: string): string {
  return `persi:native-purchase-sent:${orderId}`;
}

export function purchaseItemsStorageKey(orderId: string): string {
  return `persi:native-purchase-items:${orderId}`;
}

export function parseStoredAnalyticsItems(raw: string | null): NativeAnalyticsItem[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is NativeAnalyticsItem =>
        isRecord(item) &&
        typeof item.item_id === "string" &&
        typeof item.item_name === "string" &&
        typeof item.price === "number" &&
        typeof item.quantity === "number" &&
        (item.content_id === undefined || typeof item.content_id === "string"),
    );
  } catch {
    return [];
  }
}
